import { Store } from '../state/store';
import type { Model } from '../state/types';
import { Component } from './base';

export class ModelSelectorComponent extends Component {
    private store: Store;
    private isDropdownOpen = false;
    private oneTimeSelectCallback: ((model: string) => void) | null = null;

    constructor(container: HTMLElement, store: Store) {
        super(container);
        this.store = store;
        this.subscribeToStore();
        this.render();
        this.bindEvents();
    }

    private subscribeToStore(): void {
        this.unsubscribers.push(this.store.subscribeToModels(() => this.render()));
        this.unsubscribers.push(this.store.subscribeToSelectedModel(() => this.render()));
    }

    openDropdown(): void {
        this.isDropdownOpen = true;
        this.render();
    }

    setOneTimeSelectCallback(callback: ((model: string) => void) | null): void {
        this.oneTimeSelectCallback = callback;
    }

    private isMobile(): boolean {
        return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    }

    protected render(): void {
        const models = this.store.getModels();
        const selectedModelId = this.store.getSelectedModel();
        const selectedModel = models.find(m => m.id === selectedModelId) || models[0];
        const isMobile = this.isMobile();

        if (isMobile) {
            this.element.innerHTML = `
                <div class="model-selector">
                    <select id="model-select" aria-label="Select model">
                        ${models.map(model =>
                            `<option value="${model.id}" ${model.id === selectedModelId ? 'selected' : ''}>${model.display_name}</option>`
                        ).join('')}
                    </select>
                </div>
            `;
        } else {
            const selectedDisplayName = selectedModel?.display_name || 'Select Model';
            this.element.innerHTML = `
                <div class="model-select-wrapper">
                    <div class="model-select-trigger" id="model-select-trigger" role="button" tabindex="0" aria-haspopup="true" aria-expanded="${this.isDropdownOpen}">
                        <span class="model-select-value">${selectedDisplayName}</span>
                        <svg class="model-select-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M2.5 4.5L6 7.5L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <div class="model-select-dropdown ${this.isDropdownOpen ? 'show' : ''}" id="model-select-dropdown">
                        ${models.map(model => `
                            <div class="model-select-option ${model.id === selectedModelId ? 'selected' : ''}" 
                                 data-value="${model.id}" 
                                 role="option" 
                                 aria-selected="${model.id === selectedModelId}">
                                <span>${model.display_name}</span>
                                ${model.vision_capable ? '<i data-lucide="eye" class="model-select-vision-icon"></i>' : ''}
                            </div>
                        `).join('')}
                    </div>
                </div>
            `;
        }
        (window as any).lucide?.createIcons();
        this.bindEvents();
    }

    private bindEvents(): void {
        const isMobile = this.isMobile();

        if (isMobile) {
            const modelSelect = this.element.querySelector('#model-select') as HTMLSelectElement;
            if (modelSelect) {
                modelSelect.addEventListener('change', (e) => {
                    const newModel = (e.target as HTMLSelectElement).value;
                    this.store.setSelectedModel(newModel);
                    const conversationId = this.store.getCurrentConversationId();
                    if (conversationId) {
                        this.dispatch('update-conversation-model', {
                            conversationId,
                            model: newModel,
                        });
                    }
                });
            }
        } else {
            const trigger = this.element.querySelector('#model-select-trigger');
            const options = this.element.querySelectorAll('.model-select-option');

            if (trigger) {
                trigger.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.isDropdownOpen = !this.isDropdownOpen;
                    this.render();
                });

                trigger.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this.isDropdownOpen = !this.isDropdownOpen;
                        this.render();
                    } else if (e.key === 'Escape') {
                        this.isDropdownOpen = false;
                        this.render();
                    }
                });
            }

            options.forEach(option => {
                option.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const newModel = (e.currentTarget as HTMLElement).dataset.value!;
                    this.store.setSelectedModel(newModel);
                    this.isDropdownOpen = false;

                    if (this.oneTimeSelectCallback) {
                        const callback = this.oneTimeSelectCallback;
                        this.oneTimeSelectCallback = null;
                        callback(newModel);
                    } else {
                        const conversationId = this.store.getCurrentConversationId();
                        if (conversationId) {
                            this.dispatch('update-conversation-model', {
                                conversationId,
                                model: newModel,
                            });
                        }
                    }
                    this.render();
                });
            });

            document.addEventListener('click', (e) => {
                if (this.isDropdownOpen && !this.element.contains(e.target as Node)) {
                    this.isDropdownOpen = false;
                    this.render();
                }
            });
        }
    }

    private dispatch(type: string, detail?: any): void {
        this.element.dispatchEvent(new CustomEvent(type, { detail, bubbles: true }));
    }
}